'use client'

// The landing page's primary "Design your room" CTA.  Wraps a Next Link so
// the destination still prefetches and middle-click / cmd-click open a new
// tab the usual way, but on a normal left-click we intercept and run a
// brief curtain animation before pushing the route.
//
// The curtain is a fixed-inset overlay that fades in over ~320 ms.  Its
// final colour matches the warm app background (var(--app-bg) == the editor's
// --ed-bg), so when the destination page hydrates the swap looks continuous
// instead of the landing snapping to a different layout.  The destination
// (`/room/new`) renders its own guided shell which has slide-down chrome +
// fade-in step content, completing the motion.
//
// Without `experimental.viewTransition` enabled in next.config the
// browser View Transition API isn't wired into router pushes, so we do
// the simplest reliable thing — let the user's eye carry continuity.

import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { track } from '@/lib/analytics'
import Curtain, { CURTAIN_MS } from '@/components/ui/Curtain'

const DESTINATION = '/room/new'

interface Props {
  className?: string
  /** Defaults to `/room/new`. A seeded editor link (`/room/new?w=…`, built by
   *  components/seo/EditorCta) gets the same hand-off and analytics. */
  href?: string
  children: ReactNode
  /** Where on the page this CTA sits (hero / nav / final / problem_section).
   *  Flows to landing_cta_clicked so we can see which section converts. */
  location?: string
  /** Forwarded to the rendered anchor. JSX lets any hyphenated attribute
   *  through whether or not a component declares it, so an undeclared one is
   *  accepted at the call site and then silently dropped here — which is what
   *  the hero's `data-cta="primary"` did until the legibility suite went
   *  looking for it on the real page. */
  'data-cta'?: string
}

export default function LaunchCta({ className, href = DESTINATION, children, location, 'data-cta': dataCta }: Props) {
  const router = useRouter()
  const [leaving, setLeaving] = useState(false)

  // Prefetch the destination on mount so the curtain isn't covering a
  // cold load. Next does this automatically for visible <Link>s but the
  // explicit call also covers the case where the link is below the fold.
  useEffect(() => {
    router.prefetch(href)
  }, [router, href])

  const onClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Honour the platform's "open in new tab" gestures + middle click.
    if (
      e.defaultPrevented ||
      e.metaKey ||
      e.ctrlKey ||
      e.shiftKey ||
      e.altKey ||
      e.button !== 0
    ) {
      return
    }
    track('landing_cta_clicked', { location, destination: href })
    e.preventDefault()
    setLeaving(true)
    // Push slightly before the curtain finishes — the next route is
    // already prefetched, so the visual hand-off feels instantaneous
    // without the curtain visibly "popping" off the new page.
    window.setTimeout(() => router.push(href), CURTAIN_MS - 60)
  }

  return (
    <>
      <Link
        href={href}
        prefetch
        onClick={onClick}
        className={className}
        aria-busy={leaving || undefined}
        data-cta={dataCta}
      >
        {children}
      </Link>
      {/* Mounted only after a click, so it never steals pointer events on
          the landing page itself. The fade and the colour live in
          components/ui/Curtain — the campaign hook covers its own hand-off
          with the same component, and two scrims disagreeing about how long
          a hand-off takes is exactly the drift that sharing it prevents. */}
      {leaving && <Curtain shown />}
    </>
  )
}
